import type { TPlayerRegistrationResponse } from '@lucimark/shared/database/players-directory-schema'
import type { TRegistrationBackupDisposition } from '@/helpers/playerRegistrationAuthPolicy'
import Emittery from 'emittery'
import { SignJWT } from 'jose'
import { generate as generateShortUuid } from 'short-uuid'
import { PLAYER_REGISTRATION_STORAGE_KEY } from '@/constants/playerStorageKeys'
import { stripWebOtaForPairingPersistence } from '@/helpers/stripWebOtaForPairingPersistence'
import { getPlatformInstance } from '@/platform'

const PLAYER_REGISTRATION_KEY = PLAYER_REGISTRATION_STORAGE_KEY
const PLAYER_ID_PREFIX = 'LUCIMARK:PLAYER:ID'

// Backup keys for Tauri (Rust store + optional file). Web uses single localStorage.
const PLAYER_ID_BACKUP_KEY = 'LUCIMARK:PLAYER:ID:BACKUP'
const REGISTRATION_BACKUP_KEY = 'LUCIMARK:REGISTRATION:BACKUP'

export enum AuthenticationServiceEvent {
  REGISTRATION = 'registration'
}

export interface AuthenticationServiceEventMap {
  [AuthenticationServiceEvent.REGISTRATION]: TPlayerRegistrationResponse | null
}

export interface TSetRegistrationOptions {
  /** When clearing primary registration, whether to drop the durable backup copy (see Phase 3 / player-registration-persistence.md). */
  registrationBackup?: TRegistrationBackupDisposition
}

export interface IAuthenticationService {
  mode: string
  registrationCode: string | null
  getPlayerId: () => Promise<string>
  setRegistration: (
    registration: TPlayerRegistrationResponse | null,
    options?: TSetRegistrationOptions
  ) => Promise<void>
  getRegistration: () => Promise<TPlayerRegistrationResponse | null>
  getBearerToken: () => Promise<string>
}

export class AuthenticationService extends Emittery<AuthenticationServiceEventMap> implements IAuthenticationService {
  private _registrationCode: string | null = null
  readonly mode: string
  constructor(params: { mode: string }) {
    super()
    const { mode } = params
    this.mode = mode
  }

  get registrationCode() {
    return this._registrationCode
  }

  /**
   * Returns the stable player ID, reading from three layers in order:
   *
   * 1. Platform storage (primary) — SharedPreferences on Android, localStorage on web.
   * 2. Platform storage backup key — survives selective wipes on Android when using file backup (Phase 2).
   * 3. platform.deviceId() — ANDROID_ID on Android, stable UUID on web. Persisted so subsequent calls are cheap.
   *
   * Note: The returned id may not be a UUID (e.g. ANDROID_ID). API accepts both; see playerIdSchema.
   *
   * Android policy: prefer device id before generating a random UUID (already the order above). Do not replace a
   * persisted UUID with ANDROID_ID (or vice versa) on the client without a server-side player rebind — the tenant
   * and players directory key rows by this id; see docs/player-registration-persistence.md.
   */
  async getPlayerId(): Promise<string> {
    const platform = getPlatformInstance()
    // Layer 1: primary store
    let playerId = await platform.storage.get(PLAYER_ID_PREFIX)
    if (playerId !== null) { return playerId }

    // Layer 2: backup key
    playerId = await platform.storage.get(PLAYER_ID_BACKUP_KEY)
    if (playerId !== null) {
      await platform.storage.set(PLAYER_ID_PREFIX, playerId)
      return playerId
    }

    // Layer 2.5: AccountManager (Android only) — survives Clear Data / app data wipe.
    // The Tauri store (layers 1–2) lives in lucimark_store.json which is deleted on Clear Data;
    // AccountManager entries are stored outside app data and persist across wipes.
    if (platform.accountManagerGetPlayerId) {
      try {
        const amId = await platform.accountManagerGetPlayerId()
        if (amId !== null) {
          await this._persistPlayerId(amId)
          return amId
        }
      }
      catch {
        // Non-fatal — fall through to device ID
      }
    }

    // Layer 3: device identifier
    try {
      const deviceId = await platform.deviceId()
      if (deviceId) {
        playerId = deviceId
        await this._persistPlayerId(playerId)
        return playerId
      }
    }
    catch {
      // Fall through to UUID
    }

    playerId = generateShortUuid()
    await this._persistPlayerId(playerId)
    return playerId
  }

  private async _persistPlayerId(playerId: string) {
    const platform = getPlatformInstance()
    await platform.storage.set(PLAYER_ID_PREFIX, playerId)
    await platform.storage.set(PLAYER_ID_BACKUP_KEY, playerId).catch(() => {})
    if (platform.accountManagerSetPlayerId) {
      await platform.accountManagerSetPlayerId(playerId).catch(() => {})
    }
  }

  async setRegistration(registration: TPlayerRegistrationResponse | null, options?: TSetRegistrationOptions) {
    const platform = getPlatformInstance()
    const backupDisposition: TRegistrationBackupDisposition = options?.registrationBackup ?? 'retain'

    if (registration !== null && registration.tenantId != null) {
      const json = JSON.stringify(registration)
      await platform.storage.set(PLAYER_REGISTRATION_KEY, json)
      await platform.storage.set(REGISTRATION_BACKUP_KEY, json).catch(() => {})
      this._registrationCode = null
      await this.emit(AuthenticationServiceEvent.REGISTRATION, registration)
      return
    }

    if (registration === null) {
      await platform.storage.remove(PLAYER_REGISTRATION_KEY)
      this._registrationCode = null
      if (backupDisposition === 'remove') {
        await platform.storage.remove(REGISTRATION_BACKUP_KEY).catch(() => {})
      }
      else {
        // Ambiguous-streak clear: backup is retained. If backup holds tenant credentials,
        // promote it back to the primary key and emit it so the player never enters the
        // pairing-screen state. The stale primary credentials are already gone; the player
        // will re-authenticate using the backup on the next request, which will confirm
        // whether the credentials are still valid.
        const backupRaw = await platform.storage.get(REGISTRATION_BACKUP_KEY).catch(() => null)
        if (backupRaw !== null) {
          const backupReg = JSON.parse(backupRaw) as TPlayerRegistrationResponse
          if (backupReg.tenantId != null) {
            await platform.storage.set(PLAYER_REGISTRATION_KEY, backupRaw).catch(() => {})
            await this.emit(AuthenticationServiceEvent.REGISTRATION, backupReg)
            return
          }
        }
      }
      await this.emit(AuthenticationServiceEvent.REGISTRATION, null)
      return
    }

    // Pairing (`tenantId` null): persist payload on the primary key so `getRegistration` does not
    // immediately resurrect a prior tenant from backup; keep backup as last-known tenant snapshot.
    // Omit `webOta` from storage (JWT + version): it goes stale after deploys and bloats localStorage;
    // the player refreshes GET /registration and uses the WS handshake for OTA offers while pairing.
    const persistable = stripWebOtaForPairingPersistence(
      registration as unknown as Record<string, unknown>
    ) as TPlayerRegistrationResponse
    const json = JSON.stringify(persistable)
    await platform.storage.set(PLAYER_REGISTRATION_KEY, json)
    this._registrationCode = registration.secret ?? null
    if (backupDisposition === 'remove') {
      await platform.storage.remove(REGISTRATION_BACKUP_KEY).catch(() => {})
    }
    await this.emit(AuthenticationServiceEvent.REGISTRATION, persistable)
  }

  async getRegistration() {
    const platform = getPlatformInstance()
    const raw = await platform.storage.get(PLAYER_REGISTRATION_KEY)
    if (raw !== null) {
      return JSON.parse(raw) as TPlayerRegistrationResponse
    }
    const backup = await platform.storage.get(REGISTRATION_BACKUP_KEY)
    if (backup !== null) {
      await platform.storage.set(PLAYER_REGISTRATION_KEY, backup).catch(() => {})
      return JSON.parse(backup) as TPlayerRegistrationResponse
    }
    return null
  }

  async getBearerToken() {
    const registration = await this.getRegistration()
    const playerId = await this.getPlayerId()
    if (registration === null || registration?.tenantId === null) {
      throw new Error('unregistered')
    }
    const { secret, tenantId } = registration

    if (this.mode === 'development') {
      return btoa(JSON.stringify({ playerId, tenantId, secret }))
    }
    const jwt = await new SignJWT()
      .setProtectedHeader({ alg: 'HS256' })
      .setAudience(tenantId)
      .setIssuedAt()
      .setSubject(playerId)
      .setExpirationTime('30s')
      .sign(new TextEncoder().encode(secret))
    return jwt
  }
}

export type TAuthenticationService = AuthenticationService
